import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { base44 } from '@/api/base44Client';
import { Loader2, Plus, Pencil, Trash2, ShieldOff, Star, Search, ArrowLeft, GripVertical } from 'lucide-react';
import { Input } from '@/components/ui/input';
import ShowcaseRigFormModal from '@/components/admin/ShowcaseRigFormModal';
import { CATEGORY_LABELS } from '@/components/showcase/RigCard';
import { useAuth } from '@/lib/AuthContext';
import { useToast } from '@/components/ui/use-toast';

export default function AdminShowcaseRigs() {
  const { user: currentUser, isLoadingAuth: authLoading } = useAuth();
  const { toast } = useToast();
  const [search, setSearch] = useState('');
  const [modalRig, setModalRig] = useState(undefined);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [reordering, setReordering] = useState(false);
  const [localOrder, setLocalOrder] = useState(null);
  const queryClient = useQueryClient();

  const { data: rigs = [], isLoading } = useQuery({
    queryKey: ['admin-showcase-rigs'],
    queryFn: () => base44.entities.ShowcaseRig.list('sort_order', 200),
    enabled: !!currentUser && currentUser.role === 'admin',
  });

  const orderedRigs = (() => {
    if (!localOrder) return rigs;
    const map = new Map(rigs.map((r) => [r.id, r]));
    const out = localOrder.map((id) => map.get(id)).filter(Boolean);
    rigs.forEach((r) => { if (!localOrder.includes(r.id)) out.push(r); });
    return out;
  })();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-showcase-rigs'] });
    queryClient.invalidateQueries({ queryKey: ['showcase-rigs-all'] });
    queryClient.invalidateQueries({ queryKey: ['featured-showcase-rigs'] });
  };

  const handleDragEnd = async (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const ids = orderedRigs.map((r) => r.id);
    const [moved] = ids.splice(result.source.index, 1);
    ids.splice(result.destination.index, 0, moved);
    setLocalOrder(ids);
    setReordering(true);
    try {
      const updates = ids.map((id, i) => ({ id, sort_order: i }));
      await base44.entities.ShowcaseRig.bulkUpdate(updates);
      invalidate();
      setLocalOrder(null);
    } catch (e) {
      setLocalOrder(null);
      toast({ title: 'Could not save order', description: e.message, variant: 'destructive' });
    } finally {
      setReordering(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await base44.entities.ShowcaseRig.delete(pendingDelete.id);
      invalidate();
      setPendingDelete(null);
    } catch (e) {
      toast({ title: 'Could not delete rig', description: e.message, variant: 'destructive' });
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }
  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#0d0d0f] pt-16 flex items-center justify-center">
        <div className="text-center">
          <ShieldOff className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-bold tracking-widest">ACCESS DENIED</p>
        </div>
      </div>
    );
  }

  const filtered = orderedRigs.filter((r) => r.name?.toLowerCase().includes(search.toLowerCase()));
  const canDrag = !search.trim();

  return (
    <div className="min-h-screen bg-[#0d0d0f] pt-16">
      {modalRig !== undefined && (
        <ShowcaseRigFormModal
          rig={modalRig}
          onClose={() => setModalRig(undefined)}
          onSaved={() => { setModalRig(undefined); invalidate(); }}
        />
      )}

      {pendingDelete && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setPendingDelete(null)}>
          <div className="bg-[#111115] border border-white/10 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white font-black text-lg uppercase mb-2">Delete rig?</h3>
            <p className="text-white/50 text-sm mb-5">This removes the &ldquo;{pendingDelete.name}&rdquo; showcase page and its public URL. This cannot be undone.</p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setPendingDelete(null)} className="px-4 py-2.5 text-xs font-bold tracking-wider border border-white/15 text-white/60 hover:text-white hover:border-white/30 transition-colors">CANCEL</button>
              <button onClick={confirmDelete} className="px-4 py-2.5 text-xs font-bold tracking-wider bg-red-600 hover:bg-red-500 text-white transition-colors">DELETE</button>
            </div>
          </div>
        </div>
      )}

      <section className="py-14 px-6 lg:px-10 border-b border-white/5">
        <div className="max-w-6xl mx-auto">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-white/40 hover:text-white text-xs font-bold tracking-wider mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> BACK TO DASHBOARD
          </Link>
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <p className="text-primary text-xs font-bold tracking-widest mb-2">BOOKINGS & SIM</p>
              <h1 className="text-white font-black text-3xl md:text-4xl uppercase">Simulator Showcase</h1>
              <p className="text-white/30 text-sm mt-1">{rigs.length} rig{rigs.length !== 1 ? 's' : ''}</p>
            </div>
            <button
              onClick={() => setModalRig(null)}
              className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wider px-4 py-3 transition-colors"
            >
              <Plus className="w-4 h-4" /> ADD RIG
            </button>
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-10 py-10 max-w-6xl mx-auto">
        <div className="relative mb-6 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search rigs..."
            className="bg-[#111115] border-white/10 text-white pl-9 h-11 placeholder:text-white/20 focus:border-primary/60" />
        </div>
        {canDrag && !isLoading && filtered.length > 0 && (
          <p className="text-white/30 text-xs mb-3 flex items-center gap-1.5">
            <GripVertical className="w-3.5 h-3.5" /> Drag rows to reorder — positions save automatically and set the order on the public Simulators page.
            {reordering && <span className="text-primary">· saving…</span>}
          </p>
        )}

        {isLoading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 border border-white/5 bg-[#111115]">
            <p className="text-white/30 text-sm">No showcase rigs yet. Click "Add Rig" to create one.</p>
          </div>
        ) : canDrag ? (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="showcase-rigs">
              {(provided) => (
                <div className="border border-white/5 overflow-x-auto" ref={provided.innerRef} {...provided.droppableProps}>
                  {filtered.map((rig, index) => (
                    <Draggable key={rig.id} draggableId={rig.id} index={index} isDragDisabled={reordering}>
                      {(p) => (
                        <div ref={p.innerRef} {...p.draggableProps} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/5 items-center hover:bg-white/[0.02] min-w-[720px]">
                          <div className="col-span-1 flex items-center" {...p.dragHandleProps}>
                            <GripVertical className={`w-4 h-4 ${reordering ? 'text-white/10' : 'text-white/20 hover:text-primary'} cursor-grab active:cursor-grabbing`} />
                          </div>
                          <div className="col-span-1">
                            <div className="w-12 h-12 bg-[#0d0d0f] border border-white/5 overflow-x-auto">
                              {rig.hero_image_url ? <img src={rig.hero_image_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full carbon-bg" />}
                            </div>
                          </div>
                          <div className="col-span-4">
                            <p className="text-white text-sm font-bold">{rig.name}</p>
                            <p className="text-white/30 text-xs">/simulators/{rig.slug}</p>
                          </div>
                          <div className="col-span-2 text-white/40 text-xs font-bold">{CATEGORY_LABELS[rig.category] || rig.category}</div>
                          <div className="col-span-1 flex items-center gap-1.5">
                            {rig.featured && <span className="text-primary text-[9px] font-black tracking-widest bg-primary/10 border border-primary/20 px-2 py-0.5">FEATURED</span>}
                          </div>
                          <div className="col-span-2 flex items-center gap-1.5">
                            <Star className="w-3.5 h-3.5 text-primary fill-primary" />
                            <span className="text-white text-sm font-bold">{Number(rig.overall_rating || 0).toFixed(1)}</span>
                          </div>
                          <div className="col-span-1 flex items-center justify-end gap-1">
                            <button onClick={() => setModalRig(rig)} className="p-2 text-white/30 hover:text-white border border-white/5 hover:border-white/20 transition-all" title="Edit">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => setPendingDelete(rig)} className="p-2 text-white/20 hover:text-red-400 border border-white/5 hover:border-red-400/30 transition-all" title="Delete">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          <div className="border border-white/5 overflow-x-auto">
            {filtered.map((rig) => (
              <div key={rig.id} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/5 items-center hover:bg-white/[0.02] min-w-[720px]">
                <div className="col-span-2">
                  <div className="w-12 h-12 bg-[#0d0d0f] border border-white/5 overflow-x-auto">
                    {rig.hero_image_url ? <img src={rig.hero_image_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full carbon-bg" />}
                  </div>
                </div>
                <div className="col-span-5">
                  <p className="text-white text-sm font-bold">{rig.name}</p>
                  <p className="text-white/30 text-xs">/simulators/{rig.slug}</p>
                </div>
                <div className="col-span-2 text-white/40 text-xs font-bold">{CATEGORY_LABELS[rig.category] || rig.category}</div>
                <div className="col-span-3 flex items-center gap-1.5">
                  {rig.featured && <span className="text-primary text-[9px] font-black tracking-widest bg-primary/10 border border-primary/20 px-2 py-0.5">FEATURED</span>}
                  <Star className="w-3.5 h-3.5 text-primary fill-primary" />
                  <span className="text-white text-sm font-bold">{Number(rig.overall_rating || 0).toFixed(1)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}